import { Omit, StringKey, Untyped } from './lang';

export function indexOfBy<T, K extends keyof T>(property: K, objects: T[], value: T[K]): number {
    for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        if (obj[property] === value) {
            return i;
        }
    }
    return -1;
}

export function excludeProperties<T extends Record<string, any>, K extends StringKey<T>>(
    properties: K[],
    object: T,
): Omit<T, K> {
    const result: { [key: string]: any } = {};
    for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key) && !properties.includes(key as K)) {
            result[key] = object[key];
        }
    }
    return result as Omit<T, K>;
}

export function excludeBy<T, K extends keyof T>(property: K, objects: T[], value: T[K]): T[] {
    const result = [];
    for (const obj of objects) {
        if (obj[property] !== value) {
            result.push(obj);
        }
    }
    return result;
}

export function existsBy<T, K extends keyof T>(property: K, objects: T[], value: T[K]): boolean {
    return indexOfBy(property, objects, value) > -1;
}

export function existsByPredicate<T>(predicate: (obj: T) => boolean, objects: T[]): boolean {
    for (const obj of objects) {
        if (predicate(obj)) {
            return true;
        }
    }
    return false;
}

export function filterNulls<T>(objects: T[]): T[] {
    const arr = [];
    for (const obj of objects) {
        if (obj !== null) {
            arr.push(obj);
        }
    }
    return arr;
}

export function filterBy<T, K extends keyof T>(property: K, objects: T[], value: T[K]): T[] {
    const result: T[] = [];
    for (const obj of objects) {
        if (obj[property] === value) {
            result.push(obj);
        }
    }
    return result;
}

export function filterByPredicate<T>(predicate: (obj: T) => boolean, objects: T[]): T[] {
    const result = [];
    for (const obj of objects) {
        if (predicate(obj)) {
            result.push(obj);
        }
    }
    return result;
}

export function findBy<T, K extends keyof T>(property: K, objects: T[], value: T[K]): T | null {
    for (const obj of objects) {
        if (obj[property] === value) {
            return obj;
        }
    }
    return null;
}

export function findByPredicate<T>(predicate: (object: T) => boolean, objects: T[]): T | null {
    for (const obj of objects) {
        if (predicate(obj)) {
            return obj;
        }
    }
    return null;
}

export function findByGetter<T, U>(getter: (object: T) => U, objects: T[], value: U): T | null {
    for (const obj of objects) {
        if (getter(obj) === value) {
            return obj;
        }
    }
    return null;
}

export function groupBy<T, K extends keyof T, V extends T[K] & string>(
    property: K,
    objects: T[],
): { [p: string]: T[] } {
    /*
     * Cast the result to disregard the optional keys in the result because we don't know what groups we will get back.
     * This is because the groupBy function groups based on property value and therefor could have any number of
     * Possibilities. This is different to the groupByPredicate function in that the predicate defines what groups to
     * Use. This means that the programmer could be expecting certain groups to appear and we need to force them to
     * Perform a null/undefined check.
     */
    return groupByGetter(item => item[property] as V, objects) as { [id: string]: T[] };
}

export function groupByOrdered<T, K extends keyof T>(property: K, objects: T[]): Array<SortedEntry<T[]>> {
    return sortedEntries(groupBy(property, objects));
}

/**
 * Group a list of items by using a predicate to decide on which group each item should be placed in. The predicate
 * function should return the name of the group to place the item into.
 *
 * @example
 * const items = [1,'a',5,'b'];
 * const groups = groupByGetter(x => typeof x, items); // { number: [1,5], string: ['a','b'] }
 * @param predicate - Property retrieval callback
 * @param objects - Items to group
 * @returns The grouped values
 */
export function groupByGetter<T, TGroupKey extends string>(
    predicate: (item: T) => TGroupKey,
    objects: T[],
): { [V in TGroupKey]?: T[] } {
    const grouped: { [V in TGroupKey]?: T[] } = {};
    for (const object of objects) {
        const value = predicate(object);
        let list = grouped[value];
        if (!list) {
            list = [];
            grouped[value] = list;
        }
        list.push(object);
    }
    return grouped;
}

export function chunk<T>(chunkSize: number, objects: T[] | null | undefined): T[][] {
    const chunked = [];
    let currentChunk = [];
    if (objects) {
        for (const obj of objects) {
            currentChunk.push(obj);
            if (currentChunk.length === chunkSize) {
                chunked.push(currentChunk);
                currentChunk = [];
            }
        }
        if (currentChunk.length > 0) {
            chunked.push(currentChunk);
        }
    }
    return chunked;
}

/**
 * Map each item in an array to a new value using an async mapping function and limited concurrency.
 *
 * @example
 * // Retrieve the details for some locations based on their location ID and limit the concurrency to 6
 * const locations = chunkAsyncMap(6, locationIds, locationId => retrieveLocationDetails(locationId));
 * @param concurrency - The maximum concurrency of this operation
 * @param items - The array to map
 * @param callback - The mapping function
 * @returns A promise containing the results
 */
export async function chunkedAsyncMap<TInput, TOutput>(
    concurrency: number,
    items: TInput[],
    callback: (value: TInput, index: number, array: TInput[]) => Promise<TOutput>,
): Promise<TOutput[]> {
    const chunks = chunk(concurrency, items);
    const output: TOutput[] = [];
    for (const itemsSubset of chunks) {
        // eslint-disable-next-line no-await-in-loop
        const mappedItems = await Promise.all(itemsSubset.map(callback));
        output.push(...mappedItems);
    }
    return output;
}

export function createMap<T>(propertyGetter: (obj: T) => number | string, objects: T[]): { readonly [key: string]: T } {
    const newMap: { [key: string]: T } = {};
    for (const obj of objects) {
        const key = propertyGetter(obj);
        newMap[key] = obj;
    }
    return newMap;
}

export function createMapOfProperty<T, U>(
    keyFn: (entry: T) => string,
    valueGetter: (entry: T) => U,
    objects: T[],
): { [key: string]: U };
export function createMapOfProperty<T, U>(
    keyFn: (entry: T) => number,
    valueGetter: (entry: T) => U,
    objects: T[],
): { [key: number]: U };
export function createMapOfProperty<T, U>(
    keyFn: (entry: T) => number | string,
    valueGetter: (entry: T) => U,
    objects: T[],
): { [key: number]: U } {
    const newMap: {
        [key: string]: U;
    } = {};
    for (const obj of objects) {
        const key: number | string = keyFn(obj);
        newMap[key] = valueGetter(obj);
    }
    return newMap;
}

export function flatten<T>(arrays: T[][]): T[] {
    const flattened: T[] = [];
    for (const array of arrays) {
        extendArray(flattened, array);
    }
    return flattened;
}

export function map<T, U>(fn: (object: T, index: number) => U, objects: T[]): U[] {
    const mapped = [];
    for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        const newObj = fn(obj, i);
        mapped.push(newObj);
    }
    return mapped;
}

export function mapKeys<T>(currentMap: any, object: { [key: number]: T } | { [key: string]: T }): { [p: number]: T } {
    const newObject: { [key: number]: T } = {};
    for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
            const newKey = currentMap[key];
            newObject[newKey] = (object as Untyped)[key];
        }
    }
    return newObject;
}

export function moveUp<T>(object: T, objects: T[]): boolean {
    const index = objects.indexOf(object);
    if (index > 0) {
        const swappedIndex = index - 1;
        const swappedObject = objects[swappedIndex];
        objects[swappedIndex] = object;
        objects[index] = swappedObject;
        return true;
    } else {
        return false;
    }
}

export function moveDown<T>(object: T, objects: T[]): boolean {
    const index = objects.indexOf(object);
    if (index < objects.length - 1) {
        const swappedIndex = index + 1;
        const swappedObject = objects[swappedIndex];
        objects[swappedIndex] = object;
        objects[index] = swappedObject;
        return true;
    } else {
        return false;
    }
}

// Splits an array into two arrays, depending on if they pass a filter condition
/**
 * @deprecated - Use @deltasierra/utilities-array instead (libraries/utilities/array)
 * @param array - The array to be split
 * @param predicate - Function which filters array based on a condition. Returns a boolean
 * @returns - Two new arrays, one whose elements passed the predicate, the other those which failed
 */
export function partition<T>(array: T[], predicate: (element: T, index: number, arr: T[]) => boolean): [T[], T[]] {
    const pass: T[] = [];
    const fail: T[] = [];
    array.forEach((element, index, arr) => (predicate(element, index, arr) ? pass : fail).push(element));
    return [pass, fail];
}

export function pluckByGetter<T, U>(getter: (obj: T) => U, objects: T[]): U[] {
    const values = [];
    for (const obj of objects) {
        values.push(getter(obj));
    }
    return values;
}

export function pluckId<TId = number>(objects: Array<{ id?: TId }>): TId[] {
    const values = [];
    for (const obj of objects) {
        if (obj.id) {
            values.push(obj.id);
        }
    }
    return values;
}

export function pluck<T, K extends keyof T>(column: K, objects: T[]): Array<T[K]> {
    return pluckByGetter(obj => obj[column], objects);
}

export function setScopeProperty<TScope, K extends keyof TScope>($scope: TScope, propertyName: K) {
    return (data: TScope[K]): void => {
        $scope[propertyName] = data;
    };
}


// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function sortedKeys<T extends {}>(object: T): Array<keyof T> {
    return Object.keys(object).sort() as Array<keyof T>;
}

export interface SortedEntry<T> {
    key: string;
    value: T;
}

function getStringComparer() {
    try {
        const localeCompare = Intl.Collator();
        return (valueA: string, valueB: string) => localeCompare.compare(valueA, valueB);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
        // Safari doesn't support Intl. This implementation should work, but is slower.
        return (valueA: string, valueB: string) => valueA.localeCompare(valueB);
    }
}

type SortPredicateOrColumns<TObject> = Array<keyof TObject> | ((item: TObject) => string);
export function sortedBy<T extends { [K in keyof T]: any }>(objects: T[], predicate: SortPredicateOrColumns<T>): T[] {
    const stringCompare = getStringComparer();
    return [...objects].sort((itemA, itemB) => {
        const valueA: string[] =
            typeof predicate === 'function' ? [predicate(itemA)] : predicate.map(col => itemA[col].toString());
        const valueB: string[] =
            typeof predicate === 'function' ? [predicate(itemB)] : predicate.map(col => itemB[col].toString());
        return (
            valueA
                .map((propertyValue, index, _arr) => stringCompare(propertyValue.toString(), valueB[index].toString()))
                .filter(value => value !== 0)[0] || 0
        );
    });
}

export function sortedEntries<T>(object: { [key: number]: T }): Array<SortedEntry<T>>;
export function sortedEntries<T>(object: { [key: string]: T }): Array<SortedEntry<T>> {
    const keys = sortedKeys(object);
    const sorted = [];
    for (const key of keys) {
        const value = object[key];
        sorted.push({
            key,
            value,
        });
    }
    return sorted as Array<SortedEntry<T>>;
}

export function sum(values: number[]): number {
    let value = 0;
    for (const val of values) {
        value += val;
    }
    return value;
}

/**
 * Toggle an item in an array. Will return "true" if the value was added or "false" if it was removed.
 *
 * @example
 * const myList = [1,2,3];
 * toggle(myList, 4)    // Returns: true
 * console.log(myList)  // Output: [1,2,3,4]
 * toggle(myList, 1)    // Returns: false
 * console.log(myList)  // Output: [2,3,4]
 * @param value - Value to toggle
 * @param objects - The list of items to either add or remove from
 * @returns "true" if the value was added, "false" if the value was removed.
 */
export function toggle<T>(value: T, objects: T[]): boolean {
    const index: number = objects.indexOf(value);
    const exists: boolean = index > -1;
    if (exists) {
        objects.splice(index, 1);
    } else {
        objects.push(value);
    }
    return !exists;
}

export function toggleById<T extends { id: number }>(value: T, objects: T[]): void {
    const index = indexOfBy('id', objects, value.id);
    if (index > -1) {
        objects.splice(index, 1);
    } else {
        objects.push(value);
    }
}

interface HasOrder {
    order: number;
}

export function rectifyOrder(objects: HasOrder[]): void {
    for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        obj.order = i;
    }
}

/**
 * Remove an element from a list based on its ID. This will only remove the first occurrence.
 *
 * @param id - The ID of the element to remove
 * @param objects - The list of elements
 * @returns The deleted element or null if none was found
 */
export function removeById<T extends { id?: number }>(id: number, objects: T[]): T | null {
    const index = indexOfBy('id', objects, id);
    if (index > -1) {
        return objects.splice(index, 1)[0];
    } else {
        return null;
    }
}

export function removeObject<T>(object: T, objects: T[]): T | null {
    const index = objects.indexOf(object);
    if (index > -1) {
        return objects.splice(index, 1)[0];
    } else {
        return null;
    }
}

export function replaceOrAddObject<T>(object: T, objects: T[]): boolean {
    const index = objects.indexOf(object);
    if (index > -1) {
        objects[index] = object;
        return true;
    } else {
        objects.push(object);
        return false;
    }
}

export function extendArray<T>(dst: T[], src: T[]): void {
    Array.prototype.push.apply(dst, src);
}

export function extendArrayUnique<T>(dst: T[], src: T[]): number {
    let numAdded = 0;
    for (const obj of src) {
        if (!dst.includes(obj)) {
            dst.push(obj);
            numAdded++;
        }
    }
    return numAdded;
}

/**
 * Remove all items from the target array that appear in the source array
 *
 * @example
 * removeAll([1,2,3], [3,4,5]) // Output: [4,5]
 * @param source - The items that should be removed
 * @param target - The array to remove the items from
 * @returns An array with the items removed
 */
export function removeAll<T>(source: Readonly<T[]>, target: Readonly<T[]>): T[] {
    const itemsToRemove = createSet(source);
    return target.filter(item => !itemsToRemove.has(item));
}

/**
 * Wrap Set and provide a fallback if it doesn't exist. This implements most is available on the native Set class.
 *
 * @example
 * const set = createSet([1,2,3,4,5]);
 * set.has(1) // true
 * set.has(10) // false
 * @param source - The source array to build the set from
 * @returns The set
 */
export function createSet<T>(source: ReadonlyArray<T>): {
    add: (val: T) => void;
    has: (val: T) => boolean;
    delete: (val: T) => void;
    clear: () => void;
    readonly size: number;
} {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const globalOrWindow: { [key: string]: any } = typeof global !== 'undefined' ? global : window;
    if (typeof globalOrWindow.Set !== 'undefined') {
        return new globalOrWindow.Set(source);
    } else {
        let values: T[] = [];
        const set = {
            add(val: T) {
                if (!this.has(val)) {
                    values.push(val);
                    this.size = values.length;
                }
            },
            clear() {
                values = [];
            },
            delete(val: T) {
                values = values.filter(item => item !== val);
                this.size = values.length;
            },
            has(val: T) {
                return values.includes(val);
            },
            size: 0,
        };
        set.add = set.add.bind(set);
        set.clear = set.clear.bind(set);
        set.delete = set.delete.bind(set);
        set.has = set.has.bind(set);

        for (const item of source) {
            set.add(item);
        }

        return set;
    }
}

export function containsAll<T>(dst: T[], src: T[]): boolean {
    for (const obj of src) {
        if (!dst.includes(obj)) {
            return false;
        }
    }
    return true;
}

/**
 * Return the set difference ie (items from `src` minutes those that appear in `dist`)
 *
 * @example
 * relativeDifference([1,2,3], [1,2,3,4,5,6])
 * // Result: [4,5,6]
 * relativeDifference([1,2], [2,3,4])
 * // Result: [3,4]
 * relativeDifference([1,2,3], [4,5,6])
 * // Result: [4,5,6]
 * relativeDifference([{ id: 1 }, { id: 2 }], [{ id: 2 }, { id: 3 }], (user1, user2) => user1.id === user2.id)
 * // Result: [{ id: 3 }]
 * @param dst - Array of items to remove
 * @param src - Array of items to compare
 * @param predicate - Optional comparison predicate function for computing if two items are the same
 * @returns T[]
 */
export function relativeDifference<T>(
    dst: T[],
    src: T[],
    predicate: (item1: T, item2: T) => boolean = (valueOne, valueTwo) => valueOne === valueTwo,
): T[] {
    const diff = [];
    for (const obj of src) {
        if (findByPredicate(item => predicate(item, obj), dst) === null) {
            diff.push(obj);
        }
    }
    return diff;
}

export function setPropertyOnAll<T, K extends keyof T>(property: K, objects: T[], value: T[K]): void {
    for (const obj of objects) {
        obj[property] = value;
    }
}

/**
 * Return the fallback when value is either undefined or null.
 *
 * @example
 * useFallbackWhenUndefinedOrNull('string1', 'string2')
 * // Result: 'string1'
 * useFallbackWhenUndefinedOrNull(undefined, 'string2')
 * // Result: 'string2'
 * useFallbackWhenUndefinedOrNull('', 'string2')
 * // Result 'string2'
 * @param value - The value
 * @param fallback - The fallback values
 * @returns The original value or the fallback if the original is null or undefined
 */
export function useFallbackWhenUndefinedOrNull<T>(value: T | null | undefined, fallback: T): T {
    if (value !== undefined && value !== null) {
        return value;
    } else {
        return fallback;
    }
}
